import React from 'react';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const BlockScheduleDialog = ({
  isOpen,
  onClose,
  editingBlockSchedule,
  blockScheduleForm,
  onBlockScheduleChange,
  onSubmit,
  doctors,
  isSaving
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleFormInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    onBlockScheduleChange({
      ...blockScheduleForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleDoctorChange = (e) => {
    const doctorId = e.target.value === '' ? null : parseInt(e.target.value);
    onBlockScheduleChange({
      ...blockScheduleForm,
      doctor_id: doctorId
    });
  };

  const handleDateTimeChange = (field, value) => {
    onBlockScheduleChange({
      ...blockScheduleForm,
      [field]: value
    });
  };

  const handleQuickDateSelect = (type) => {
    const now = dayjs();
    let blockFrom, blockUntil;

    switch (type) {
      case 'today':
        blockFrom = now.startOf('day').format('YYYY-MM-DD');
        blockUntil = now.endOf('day').format('YYYY-MM-DD');
        break;
      case 'tomorrow':
        blockFrom = now.add(1, 'day').startOf('day').format('YYYY-MM-DD');
        blockUntil = now.add(1, 'day').endOf('day').format('YYYY-MM-DD');
        break;
      case 'thisWeek':
        blockFrom = now.startOf('week').format('YYYY-MM-DD');
        blockUntil = now.endOf('week').format('YYYY-MM-DD');
        break;
      case 'nextWeek':
        blockFrom = now.add(1, 'week').startOf('week').format('YYYY-MM-DD');
        blockUntil = now.add(1, 'week').endOf('week').format('YYYY-MM-DD');
        break;
      default:
        return;
    }

    onBlockScheduleChange({
      ...blockScheduleForm,
      block_from: blockFrom,
      block_until: blockUntil
    });
  };

  const inputBase =
    'w-full px-3 py-2.5 text-gray-900 border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-shadow duration-200';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-6 pb-4 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">
            {editingBlockSchedule ? t('blockScheduleDialog.editBlockSchedule') : t('blockScheduleDialog.addBlockSchedule')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -m-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('blockScheduleDialog.selectDoctor')}
            </label>
            <select
              name="doctor_id"
              value={blockScheduleForm.doctor_id || ''}
              onChange={handleDoctorChange}
              className={inputBase}
            >
              <option value="">{t('blockScheduleDialog.allDoctors')}</option>
              {doctors.map(doctor => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('blockScheduleDialog.reason')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="reason"
              value={blockScheduleForm.reason || ''}
              onChange={handleFormInputChange}
              className={inputBase}
              placeholder={t('blockScheduleDialog.reasonPlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('blockScheduleDialog.blockFrom')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="block_from"
              value={blockScheduleForm.block_from ? String(blockScheduleForm.block_from).slice(0, 10) : ''}
              onChange={(e) => handleDateTimeChange('block_from', e.target.value)}
              className={inputBase}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('blockScheduleDialog.blockUntil')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="block_until"
              value={blockScheduleForm.block_until ? String(blockScheduleForm.block_until).slice(0, 10) : ''}
              onChange={(e) => handleDateTimeChange('block_until', e.target.value)}
              className={inputBase}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('blockScheduleDialog.quickSelect')}
            </label>
            <div className="flex flex-wrap gap-2">
              {['today', 'tomorrow', 'thisWeek', 'nextWeek'].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleQuickDateSelect(key)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 transition-colors"
                >
                  {t(`blockScheduleDialog.${key}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2.5 bg-amber-500 text-white font-medium rounded-lg shadow-sm hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSaving ? t('common.processing') : editingBlockSchedule ? t('blockScheduleDialog.updateBlockSchedule') : t('blockScheduleDialog.createBlockSchedule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlockScheduleDialog;
